
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function PendantLightingInstallationPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://electriciansolutionsfresno.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Pendant Lighting Installation',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Professional pendant light installation in Fresno, CA. Kitchen island pendants, dining room fixtures, and decorative hanging lights installed by licensed electricians.',
    url: `${siteUrl}/services/lighting-outdoor/pendant-lighting-installation`,
  };

  const features = [
    {
      icon: 'ri-lightbulb-line',
      title: 'Kitchen Island Pendants',
      description: 'Stylish task lighting over kitchen islands and breakfast bars',
    },
    {
      icon: 'ri-restaurant-line',
      title: 'Dining Room Fixtures',
      description: 'Elegant pendant lights that create perfect dining ambiance',
    },
    {
      icon: 'ri-home-smile-line',
      title: 'Entryway Lighting',
      description: 'Make a statement with dramatic pendant fixtures in foyers',
    },
    {
      icon: 'ri-contrast-2-line',
      title: 'Dimmable Installation',
      description: 'Control brightness for any occasion with dimmer switches',
    },
    {
      icon: 'ri-ruler-line',
      title: 'Proper Height Adjustment',
      description: 'Expert placement at optimal heights for function and aesthetics',
    },
    {
      icon: 'ri-tools-line',
      title: 'Heavy Fixture Support',
      description: 'Reinforced mounting for large, heavy pendant lights',
    },
  ];

  return (
    <>
      <SEOHead
        title="Pendant Light Installation Fresno CA | Kitchen Island Lighting"
        description="Professional pendant lighting installation in Fresno, CA. Kitchen island pendants, dining room fixtures, and decorative hanging lights. Licensed electricians."
        canonical="/services/lighting-outdoor/pendant-lighting-installation"
        keywords="pendant lighting Fresno, kitchen island lights, dining room fixtures, hanging lights"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Pendant Lighting Installation — Fresno, CA"
        heroSubtitle="Elegant pendant light installation for Fresno kitchens, dining rooms, and entryways"
        heroImage="https://public.readdy.ai/ai/img_res/f70d6bed88a7a76319e09947d3cccd86.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Close%20up%20of%20stylish%20modern%20pendant%20light%20fixture%20with%20glass%20shade%20hanging%20over%20kitchen%20island%20with%20warm%20LED%20bulb%20glowing%20California%20home%20interior%20no%20people%20present&width=700&height=600&seq=pendant-light-fresno-005&orientation=portrait"
        fresnoContent="Add style and functionality to your Fresno home with professionally installed pendant lighting. From modern kitchen islands in Fig Garden to elegant dining rooms in the Tower District, we install pendant fixtures that combine beauty with perfect task lighting. Our licensed electricians ensure proper height placement, secure mounting for heavy fixtures, and seamless integration with dimmer controls. Whether you prefer industrial, farmhouse, or contemporary styles, we bring your lighting vision to life."
        features={features}
        faqs={[
          {
            question: 'How high should pendant lights hang over a kitchen island in Fresno?',
            answer:
              'For Fresno kitchen islands, pendant lights should hang 30–36 inches above the countertop surface. This height provides optimal task lighting without obstructing sight lines across the kitchen. For dining tables, pendants should hang 30–34 inches above the table surface. Ceiling height also matters—homes with 9+ foot ceilings may require longer pendant drops. We assess your specific space during consultation and install pendants at the perfect height for both function and aesthetics in your Fresno home.',
          },
          {
            question: 'How much does pendant light installation cost in Fresno?',
            answer:
              'Pendant light installation in Fresno typically costs $200–$500 per fixture depending on complexity and weight. Simple single-pendant installations start around $200–$300, while multi-pendant installations over kitchen islands (3 fixtures) range from $600–$1,200. Heavy or complex fixtures requiring reinforced mounting cost $400–$600 per light. Prices include all electrical work, mounting hardware, and testing. We provide free consultations and detailed quotes for all Fresno projects.',
          },
          {
            question: 'Can you install pendant lights where no fixture exists in Fresno homes?',
            answer:
              'Yes! We install pendant lights in new locations throughout Fresno homes. This involves running new electrical wiring through attic or ceiling spaces, installing junction boxes, and ensuring proper support for the fixture weight. New pendant installations require electrical permits in Fresno, which we handle for you. This service is popular for kitchen island lighting, dining room upgrades, and entryway statements. We assess your ceiling structure and electrical system to determine the best installation approach.',
          },
          {
            question: 'What pendant light styles work best in Fresno homes?',
            answer:
              "Pendant light styles depend on your Fresno home's design. Modern and contemporary homes in North Fresno look great with sleek glass or metal pendants. Farmhouse and transitional styles popular in the Tower District pair well with industrial or rustic pendants. For dining rooms, drum pendants or chandeliers create elegant ambiance. Kitchen islands typically use 2–3 matching pendants for balanced lighting. We help you select fixtures that complement your home's architecture and your personal style during consultation.",
          },
          {
            question: 'Should pendant lights be on a dimmer switch in Fresno?',
            answer:
              'Absolutely! We strongly recommend dimmer switches for pendant lights in Fresno homes. Dimmers allow you to adjust brightness for different activities—bright task lighting for cooking and food prep, medium lighting for casual dining, and low ambient lighting for entertaining. Dimming also extends bulb life and saves energy. We install LED-compatible dimmer switches that provide smooth, flicker-free control. Many Fresno homeowners also choose smart dimmers for voice and app control.',
          },
          {
            question: 'How long does pendant light installation take in Fresno?',
            answer:
              'Most pendant light installations in Fresno are completed in 2–4 hours per fixture. Installing 3 pendants over a kitchen island typically takes 4–6 hours including electrical work, mounting, and dimmer installation. New installations requiring wiring from scratch may take 6–8 hours. We work efficiently to minimize disruption and always clean up thoroughly. Many Fresno homeowners schedule pendant installations during kitchen renovations for seamless integration with other upgrades.',
          },
        ]}
      />
    </>
  );
}
